/**
 * useHomeSLHomeBlocksLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeSLHomeBlocks
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeSLAlmadarHomeView as HomeSLAlmadarHomeViewEntity, HomeSLHomeBlocksEventPayloadMap } from '@app/shared';
import { useUIEvents } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import { useTranslate } from '@almadar/ui/hooks';

// ============================================================================
// State Machine
// ============================================================================

export type HomeSLHomeBlocksState =
  | 'idle';

export type HomeSLHomeBlocksEvent =
  | 'INIT';

type HomeSLHomeBlocksSlot = 'main';
interface HomeSLHomeBlocksInternalState {
  machineState: HomeSLHomeBlocksState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeSLHomeBlocksEvent | null;
  slotWriter: Partial<Record<HomeSLHomeBlocksSlot, HomeSLHomeBlocksEvent | null>>;
  loading: boolean;
  pendingEvent: HomeSLHomeBlocksEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLAlmadarHomeViewEntity>;
}

type HomeSLHomeBlocksAction =
  | { type: 'EVENT_SUCCESS'; event: HomeSLHomeBlocksEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeSLHomeBlocksSlot[]; renderClear?: HomeSLHomeBlocksSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeSLHomeBlocksEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeSLAlmadarHomeViewEntity & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeSLHomeBlocksReducer(
  state: HomeSLHomeBlocksInternalState,
  action: HomeSLHomeBlocksAction
): HomeSLHomeBlocksInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeSLHomeBlocksState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeSLAlmadarHomeViewEntity>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeSLAlmadarHomeViewEntity>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeSLAlmadarHomeViewEntity>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeSLHomeBlocksInternalState = {
  machineState: 'idle',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface HomeSLHomeBlocksConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
  alignment: 'center' | 'start';
  columns: number;
  edgeBottom: 'none' | 'curve' | 'wave' | 'arc' | 'tilt' | 'step' | 'scallop';
  edgeColor: 'background' | 'muted' | 'surface' | 'gradient' | 'inverse';
  edgeTop: 'none' | 'curve' | 'wave' | 'arc' | 'tilt' | 'step' | 'scallop';
  entries: { icon: string; name: string[]; role: string[] }[];
  heading?: string;
  lede?: string;
  markContent: { bg: Array<Array<Array<string | string[]> | string | string[]> | string | string[]>; children: { children: { align: Array<string | string[]>; children: Array<{ align: Array<string | string[]>; className: Array<string | string[]>; color: string; content: string; type: string; variant: string } | { align: Array<string | string[]>; className: Array<string | string[]>; content: string; type: string; variant: string } | { items: Array<Array<string | { logo: string[]; name: string[] }> | string>; type: string; variant: Array<string | string[]> }>; direction: string; gap: string; type: string }[]; maxWidth: string; padding: string; size: string; type: string }[]; edgeBottom: string; edgeColor: string; edgeTop: string; fullWidth: boolean; paddingY: string; texture: string; type: string };
  markIcon: string;
  marqueeContent: { children: { children: Array<{ children: { align: string; children: Array<{ align: string; className: string; color: string; content: string; type: string; variant: string } | { align: string; className: string; content: string; type: string; variant: string; weight: string }>; direction: string; gap: string; type: string }[]; maxWidth: string; padding: string; size: string; type: string } | { children: { children: Array<{ align: string; "aria-hidden": boolean; children: Array<Array<number | string> | Array<string | { align: string; children: Array<{ children: Array<{ className: string; color: string; content: string[]; type: string; variant: string } | { content: string[]; type: string; variant: string; weight: string }>; direction: string; gap: string; type: string } | { children: { color: string; name: string[]; size: string; type: string }[]; className: string; type: string }>; className: string; direction: string; gap: string; type: string }> | string>; className: string; direction: string; gap: string; type: string } | { align: string; children: Array<Array<number | string> | Array<string | { align: string; children: Array<{ children: Array<{ className: string; color: string; content: string[]; type: string; variant: string } | { content: string[]; type: string; variant: string; weight: string }>; direction: string; gap: string; type: string } | { children: { color: string; name: string[]; size: string; type: string }[]; className: string; type: string }>; className: string; direction: string; gap: string; type: string }> | string>; direction: string; gap: string; type: string }>; className: string; type: string }[]; className: string; fullWidth: boolean; type: string }>; direction: string; gap: string; type: string }[]; fullWidth: boolean; paddingY: string; type: string };
  proofLook: 'testimonials' | 'portfolio' | 'logos' | 'marquee' | 'badges';
  surface: 'plain' | 'muted' | 'accent' | 'inverse';
  texture: boolean;
}

const DEFAULT_HOME_SL_HOME_BLOCKS_CONFIG = {
  alignment: "center",
  columns: 3,
  edgeBottom: "none",
  edgeColor: "background",
  edgeTop: "none",
  entries: [{ icon: "calculator", name: ["i18n/t", "std-almadar-home:catalog.accounting.name"], role: ["i18n/t", "std-almadar-home:catalog.accounting.line"] }, { icon: "network", name: ["i18n/t", "std-almadar-home:catalog.apiGateway.name"], role: ["i18n/t", "std-almadar-home:catalog.apiGateway.line"] }, { icon: "user-search", name: ["i18n/t", "std-almadar-home:catalog.recruiting.name"], role: ["i18n/t", "std-almadar-home:catalog.recruiting.line"] }, { icon: "calendar-check", name: ["i18n/t", "std-almadar-home:catalog.booking.name"], role: ["i18n/t", "std-almadar-home:catalog.booking.line"] }, { icon: "gauge", name: ["i18n/t", "std-almadar-home:catalog.capacity.name"], role: ["i18n/t", "std-almadar-home:catalog.capacity.line"] }, { icon: "git-branch", name: ["i18n/t", "std-almadar-home:catalog.cicd.name"], role: ["i18n/t", "std-almadar-home:catalog.cicd.line"] }, { icon: "newspaper", name: ["i18n/t", "std-almadar-home:catalog.cms.name"], role: ["i18n/t", "std-almadar-home:catalog.cms.line"] }, { icon: "code", name: ["i18n/t", "std-almadar-home:catalog.academy.name"], role: ["i18n/t", "std-almadar-home:catalog.academy.line"] }, { icon: "hard-hat", name: ["i18n/t", "std-almadar-home:catalog.construction.name"], role: ["i18n/t", "std-almadar-home:catalog.construction.line"] }, { icon: "file-signature", name: ["i18n/t", "std-almadar-home:catalog.contracts.name"], role: ["i18n/t", "std-almadar-home:catalog.contracts.line"] }, { icon: "handshake", name: ["i18n/t", "std-almadar-home:catalog.crm.name"], role: ["i18n/t", "std-almadar-home:catalog.crm.line"] }, { icon: "heart-handshake", name: ["i18n/t", "std-almadar-home:catalog.success.name"], role: ["i18n/t", "std-almadar-home:catalog.success.line"] }, { icon: "activity", name: ["i18n/t", "std-almadar-home:catalog.devops.name"], role: ["i18n/t", "std-almadar-home:catalog.devops.line"] }, { icon: "folder-open", name: ["i18n/t", "std-almadar-home:catalog.documents.name"], role: ["i18n/t", "std-almadar-home:catalog.documents.line"] }, { icon: "shopping-bag", name: ["i18n/t", "std-almadar-home:catalog.store.name"], role: ["i18n/t", "std-almadar-home:catalog.store.line"] }, { icon: "ticket", name: ["i18n/t", "std-almadar-home:catalog.events.name"], role: ["i18n/t", "std-almadar-home:catalog.events.line"] }, { icon: "bar-chart-3", name: ["i18n/t", "std-almadar-home:catalog.executive.name"], role: ["i18n/t", "std-almadar-home:catalog.executive.line"] }, { icon: "wrench", name: ["i18n/t", "std-almadar-home:catalog.fieldService.name"], role: ["i18n/t", "std-almadar-home:catalog.fieldService.line"] }, { icon: "wallet", name: ["i18n/t", "std-almadar-home:catalog.finance.name"], role: ["i18n/t", "std-almadar-home:catalog.finance.line"] }, { icon: "dumbbell", name: ["i18n/t", "std-almadar-home:catalog.fitness.name"], role: ["i18n/t", "std-almadar-home:catalog.fitness.line"] }, { icon: "truck", name: ["i18n/t", "std-almadar-home:catalog.fleet.name"], role: ["i18n/t", "std-almadar-home:catalog.fleet.line"] }, { icon: "messages-square", name: ["i18n/t", "std-almadar-home:catalog.forum.name"], role: ["i18n/t", "std-almadar-home:catalog.forum.line"] }, { icon: "stethoscope", name: ["i18n/t", "std-almadar-home:catalog.clinic.name"], role: ["i18n/t", "std-almadar-home:catalog.clinic.line"] }, { icon: "life-buoy", name: ["i18n/t", "std-almadar-home:catalog.helpdesk.name"], role: ["i18n/t", "std-almadar-home:catalog.helpdesk.line"] }, { icon: "users", name: ["i18n/t", "std-almadar-home:catalog.hr.name"], role: ["i18n/t", "std-almadar-home:catalog.hr.line"] }, { icon: "clipboard-check", name: ["i18n/t", "std-almadar-home:catalog.inspection.name"], role: ["i18n/t", "std-almadar-home:catalog.inspection.line"] }, { icon: "package", name: ["i18n/t", "std-almadar-home:catalog.inventory.name"], role: ["i18n/t", "std-almadar-home:catalog.inventory.line"] }, { icon: "cpu", name: ["i18n/t", "std-almadar-home:catalog.iot.name"], role: ["i18n/t", "std-almadar-home:catalog.iot.line"] }, { icon: "scale", name: ["i18n/t", "std-almadar-home:catalog.legal.name"], role: ["i18n/t", "std-almadar-home:catalog.legal.line"] }, { icon: "map-pin", name: ["i18n/t", "std-almadar-home:catalog.listings.name"], role: ["i18n/t", "std-almadar-home:catalog.listings.line"] }, { icon: "graduation-cap", name: ["i18n/t", "std-almadar-home:catalog.lms.name"], role: ["i18n/t", "std-almadar-home:catalog.lms.line"] }, { icon: "megaphone", name: ["i18n/t", "std-almadar-home:catalog.campaigns.name"], role: ["i18n/t", "std-almadar-home:catalog.campaigns.line"] }, { icon: "store", name: ["i18n/t", "std-almadar-home:catalog.marketplace.name"], role: ["i18n/t", "std-almadar-home:catalog.marketplace.line"] }, { icon: "hand-heart", name: ["i18n/t", "std-almadar-home:catalog.donations.name"], role: ["i18n/t", "std-almadar-home:catalog.donations.line"] }, { icon: "notebook-pen", name: ["i18n/t", "std-almadar-home:catalog.notes.name"], role: ["i18n/t", "std-almadar-home:catalog.notes.line"] }, { icon: "folder-kanban", name: ["i18n/t", "std-almadar-home:catalog.projects.name"], role: ["i18n/t", "std-almadar-home:catalog.projects.line"] }, { icon: "building-2", name: ["i18n/t", "std-almadar-home:catalog.property.name"], role: ["i18n/t", "std-almadar-home:catalog.property.line"] }, { icon: "book-open", name: ["i18n/t", "std-almadar-home:catalog.helpCenter.name"], role: ["i18n/t", "std-almadar-home:catalog.helpCenter.line"] }, { icon: "message-circle", name: ["i18n/t", "std-almadar-home:catalog.chat.name"], role: ["i18n/t", "std-almadar-home:catalog.chat.line"] }, { icon: "utensils", name: ["i18n/t", "std-almadar-home:catalog.restaurant.name"], role: ["i18n/t", "std-almadar-home:catalog.restaurant.line"] }, { icon: "scan-barcode", name: ["i18n/t", "std-almadar-home:catalog.retail.name"], role: ["i18n/t", "std-almadar-home:catalog.retail.line"] }, { icon: "rss", name: ["i18n/t", "std-almadar-home:catalog.social.name"], role: ["i18n/t", "std-almadar-home:catalog.social.line"] }, { icon: "credit-card", name: ["i18n/t", "std-almadar-home:catalog.billing.name"], role: ["i18n/t", "std-almadar-home:catalog.billing.line"] }, { icon: "clipboard-list", name: ["i18n/t", "std-almadar-home:catalog.survey.name"], role: ["i18n/t", "std-almadar-home:catalog.survey.line"] }, { icon: "list-checks", name: ["i18n/t", "std-almadar-home:catalog.tasks.name"], role: ["i18n/t", "std-almadar-home:catalog.tasks.line"] }, { icon: "timer", name: ["i18n/t", "std-almadar-home:catalog.time.name"], role: ["i18n/t", "std-almadar-home:catalog.time.line"] }, { icon: "candlestick-chart", name: ["i18n/t", "std-almadar-home:catalog.trading.name"], role: ["i18n/t", "std-almadar-home:catalog.trading.line"] }, { icon: "library", name: ["i18n/t", "std-almadar-home:catalog.wiki.name"], role: ["i18n/t", "std-almadar-home:catalog.wiki.line"] }],
  markContent: { bg: ["if", ["==", "@config.surface", "muted"], "muted", ["if", ["==", "@config.surface", "accent"], "gradient", ["if", ["==", "@config.surface", "inverse"], "inverse", "transparent"]]], children: [{ children: [{ align: ["if", ["==", "@config.alignment", "center"], "center", "start"], children: [{ align: ["if", ["==", "@config.alignment", "center"], "center", "left"], className: ["if", ["==", "@config.heading", ""], "hidden", "text-balance"], content: "@config.heading", type: "typography", variant: "h2" }, { align: ["if", ["==", "@config.alignment", "center"], "center", "left"], className: ["if", ["==", "@config.lede", ""], "hidden", "max-w-2xl text-pretty"], color: "muted", content: "@config.lede", type: "typography", variant: "body" }, { items: ["array/map", "@config.entries", ["fn", "item", { logo: ["object/get", "@item", "logo", ""], name: ["object/get", "@item", "name", ""] }]], type: "social-proof", variant: ["if", ["==", "@config.proofLook", "badges"], "badges", "logos"] }], direction: "vertical", gap: "lg", type: "stack" }], maxWidth: "xl", padding: "lg", size: "xl", type: "container" }], edgeBottom: "@config.edgeBottom", edgeColor: "@config.edgeColor", edgeTop: "@config.edgeTop", fullWidth: true, paddingY: "xl", texture: "@config.texture", type: "box" },
  markIcon: "building-2",
  marqueeContent: { children: [{ children: [{ children: [{ align: "center", children: [{ align: "center", className: "text-balance", content: "@config.heading", type: "typography", variant: "h2", weight: "bold" }, { align: "center", className: "max-w-2xl text-pretty", color: "muted", content: "@config.lede", type: "typography", variant: "large" }], direction: "vertical", gap: "sm", type: "stack" }], maxWidth: "xl", padding: "lg", size: "xl", type: "container" }, { children: [{ children: [{ align: "stretch", children: ["array/map", ["array/take", "@config.entries", 24], ["fn", "item", { align: "start", children: [{ children: [{ color: "primary", name: ["object/get", "@item", "icon", "blocks"], size: "md", type: "icon" }], className: "w-10 h-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center", type: "box" }, { children: [{ content: ["object/get", "@item", "name", ""], type: "typography", variant: "body", weight: "semibold" }, { className: "whitespace-normal", color: "muted", content: ["object/get", "@item", "role", ""], type: "typography", variant: "small" }], direction: "vertical", gap: "xs", type: "stack" }], className: "shrink-0 w-80 mx-2 rounded-xl border border-border bg-card p-5 shadow-elevation-card", direction: "horizontal", gap: "md", type: "stack" }]], direction: "horizontal", gap: "none", type: "stack" }, { align: "stretch", "aria-hidden": true, children: ["array/map", ["array/take", "@config.entries", 24], ["fn", "item", { align: "start", children: [{ children: [{ color: "primary", name: ["object/get", "@item", "icon", "blocks"], size: "md", type: "icon" }], className: "w-10 h-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center", type: "box" }, { children: [{ content: ["object/get", "@item", "name", ""], type: "typography", variant: "body", weight: "semibold" }, { className: "whitespace-normal", color: "muted", content: ["object/get", "@item", "role", ""], type: "typography", variant: "small" }], direction: "vertical", gap: "xs", type: "stack" }], className: "shrink-0 w-80 mx-2 rounded-xl border border-border bg-card p-5 shadow-elevation-card", direction: "horizontal", gap: "md", type: "stack" }]], className: "motion-reduce:hidden", direction: "horizontal", gap: "none", type: "stack" }], className: "proof-marquee-track animate-almadar-marquee [animation-duration:140s] hover:[animation-play-state:paused] motion-reduce:animate-none motion-reduce:w-auto motion-reduce:flex-wrap motion-reduce:justify-center", type: "box" }], className: "proof-marquee py-1 motion-reduce:[mask-image:none]", fullWidth: true, type: "box" }, { children: [{ children: [{ align: "stretch", children: ["array/map", ["array/drop", "@config.entries", 24], ["fn", "item", { align: "start", children: [{ children: [{ color: "primary", name: ["object/get", "@item", "icon", "blocks"], size: "md", type: "icon" }], className: "w-10 h-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center", type: "box" }, { children: [{ content: ["object/get", "@item", "name", ""], type: "typography", variant: "body", weight: "semibold" }, { className: "whitespace-normal", color: "muted", content: ["object/get", "@item", "role", ""], type: "typography", variant: "small" }], direction: "vertical", gap: "xs", type: "stack" }], className: "shrink-0 w-80 mx-2 rounded-xl border border-border bg-card p-5 shadow-elevation-card", direction: "horizontal", gap: "md", type: "stack" }]], direction: "horizontal", gap: "none", type: "stack" }, { align: "stretch", "aria-hidden": true, children: ["array/map", ["array/drop", "@config.entries", 24], ["fn", "item", { align: "start", children: [{ children: [{ color: "primary", name: ["object/get", "@item", "icon", "blocks"], size: "md", type: "icon" }], className: "w-10 h-10 shrink-0 rounded-lg bg-primary/10 flex items-center justify-center", type: "box" }, { children: [{ content: ["object/get", "@item", "name", ""], type: "typography", variant: "body", weight: "semibold" }, { className: "whitespace-normal", color: "muted", content: ["object/get", "@item", "role", ""], type: "typography", variant: "small" }], direction: "vertical", gap: "xs", type: "stack" }], className: "shrink-0 w-80 mx-2 rounded-xl border border-border bg-card p-5 shadow-elevation-card", direction: "horizontal", gap: "md", type: "stack" }]], className: "motion-reduce:hidden", direction: "horizontal", gap: "none", type: "stack" }], className: "proof-marquee-track animate-almadar-marquee [animation-duration:140s] hover:[animation-play-state:paused] motion-reduce:animate-none motion-reduce:w-auto motion-reduce:flex-wrap motion-reduce:justify-center [animation-direction:reverse]", type: "box" }], className: "proof-marquee py-1 motion-reduce:[mask-image:none]", fullWidth: true, type: "box" }], direction: "vertical", gap: "lg", type: "stack" }], fullWidth: true, paddingY: "none", type: "box" },
  proofLook: "marquee",
  surface: "plain",
  texture: false,
} as HomeSLHomeBlocksConfig;

export interface HomeSLHomeBlocksLogicReturn {
  state: HomeSLHomeBlocksState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeSLHomeBlocksEvent | null;
  slotWriter: Partial<Record<HomeSLHomeBlocksSlot, HomeSLHomeBlocksEvent | null>>;
  loading: boolean;
  pendingEvent: HomeSLHomeBlocksEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeSLAlmadarHomeViewEntity>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeSLHomeBlocksEventPayloadMap]: (payload?: HomeSLHomeBlocksEventPayloadMap[K]) => Promise<EventResponse> };
  config: HomeSLHomeBlocksConfig;
}

export function useHomeSLHomeBlocksLogic(config?: Partial<HomeSLHomeBlocksConfig>): HomeSLHomeBlocksLogicReturn {
  const mergedConfig: HomeSLHomeBlocksConfig = { ...DEFAULT_HOME_SL_HOME_BLOCKS_CONFIG, ...(config ?? {}) };

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-sl-home-blocks', orbitalName: 'HomeSL', traitName: 'HomeSLHomeBlocks' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeSLHomeBlocksReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  const { t: __t, locale: __locale } = useTranslate();
  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['HomeSLAlmadarHomeView'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
        if (false) {

        } else {

          if (false) {

          } else {

            if (true) {

            } else {

            }

          }

        }

      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeSLHomeBlocksSlot[]; clear: HomeSLHomeBlocksSlot[] } => {
    switch (fromState) {
      case 'idle':
        switch (event) {
          case 'INIT': return { write: ['main'] as HomeSLHomeBlocksSlot[], clear: [] as HomeSLHomeBlocksSlot[] };
          default: return { write: [], clear: [] };
        }
      default:
        return { write: [], clear: [] };
    }
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeSLAlmadarHomeView': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeSLHomeBlocksEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    void sendEvent; void commandPump; // no server-touching effect on this trait; shared-store commit above is the only sync needed
    return { success: true, transitioned: true, states: { 'HomeSLHomeBlocks': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeSLHomeBlocksEventPayloadMap]: (payload?: HomeSLHomeBlocksEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
  };

  useUIEvents(enqueueEvent, 'HomeSL.HomeSLHomeBlocks', ['INIT'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeSLHomeBlocks', (): TraitStateSnapshot => ({
      traitName: 'HomeSLHomeBlocks',
      currentState: internalStateRef.current.machineState,
      states: ['idle'],
      events: ['INIT'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: [],
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  mergedConfig.heading = mergedConfig.heading ?? (__t("std-almadar-home:catalog.heading"));
  mergedConfig.lede = mergedConfig.lede ?? (__t("std-almadar-home:catalog.lede"));
  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
